"use client";

import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";
import { useEffect } from "react";
import { comparisonService } from "@/lib/services/comparisonService";

type Product = {
  id: string;
  category: string;
  name: string;
  nameZh: string;
  brand: string;
  specs: string;
  image: string;
  alt: string;
  badge: string;
  badgeColor: string;
  moq: string;
};

interface ComparisonTableProps {
  products: Product[];
  onClose: () => void;
}

export default function ComparisonTable({ products, onClose }: ComparisonTableProps) {
  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    // Save comparison to Supabase
    if (products.length >= 2) {
      comparisonService.saveComparison(
        products.map((p) => ({ id: p.id, name: p.name }))
      );
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [products]);

  // Parse specs string into individual spec items
  const parseSpecs = (specs: string) => {
    return specs.split("/").map((s) => s.trim());
  };

  // Get all unique spec categories across all products
  const maxSpecCount = Math.max(...products.map((p) => parseSpecs(p.specs).length));

  return (
    <div className="fixed inset-0 bg-navy-dark/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-4xl max-w-7xl w-full max-h-[90vh] overflow-hidden shadow-navy-lg animate-scale-in">
        {/* Header */}
        <div className="bg-navy text-white px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="ScaleIcon" size={28} className="text-orange" />
            <div>
              <h2 className="font-heading font-black text-2xl">Product Comparison</h2>
              <p className="text-white/70 text-sm">產品規格對比</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
            aria-label="Close comparison"
          >
            <Icon name="XMarkIcon" size={32} />
          </button>
        </div>

        {/* Comparison Table */}
        <div className="overflow-auto max-h-[calc(90vh-120px)]">
          <table className="w-full">
            <thead className="bg-bg-light sticky top-0 z-10">
              <tr>
                <th className="text-left px-6 py-4 font-heading font-bold text-navy text-sm uppercase tracking-wider w-48">
                  Specification
                </th>
                {products.map((product) => (
                  <th key={product.id} className="px-6 py-4 min-w-[280px]">
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative w-32 h-32 rounded-2xl overflow-hidden">
                        <AppImage
                          src={product.image}
                          alt={product.alt}
                          fill
                          className="object-cover"
                          sizes="128px"
                        />
                      </div>
                      <div className="text-center">
                        <span className={`text-xs font-black px-3 py-1 rounded-full ${product.badgeColor} inline-block mb-2`}>
                          {product.badge}
                        </span>
                        <h3 className="font-heading font-black text-navy text-base leading-tight mb-1">
                          {product.name}
                        </h3>
                        <p className="text-xs text-text-muted mb-1">{product.nameZh}</p>
                        <p className="text-xs font-bold text-orange uppercase">{product.brand}</p>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Category Row */}
              <tr className="border-t border-border">
                <td className="px-6 py-4 font-bold text-navy text-sm bg-navy-50">
                  <div className="flex items-center gap-2">
                    <Icon name="TagIcon" size={16} />
                    Category
                  </div>
                </td>
                {products.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center text-sm text-text-mid">
                    {product.category.toUpperCase()}
                  </td>
                ))}
              </tr>

              {/* Specifications Rows */}
              {Array.from({ length: maxSpecCount }).map((_, index) => (
                <tr key={index} className="border-t border-border hover:bg-bg-light transition-colors">
                  <td className="px-6 py-4 font-bold text-navy text-sm bg-navy-50">
                    <div className="flex items-center gap-2">
                      <Icon name="CpuChipIcon" size={16} />
                      Spec {index + 1}
                    </div>
                  </td>
                  {products.map((product) => {
                    const specs = parseSpecs(product.specs);
                    return (
                      <td key={product.id} className="px-6 py-4 text-center text-sm font-mono text-text-mid">
                        {specs[index] || "-"}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* MOQ Row */}
              <tr className="border-t border-border">
                <td className="px-6 py-4 font-bold text-navy text-sm bg-navy-50">
                  <div className="flex items-center gap-2">
                    <Icon name="CubeIcon" size={16} />
                    Minimum Order
                  </div>
                </td>
                {products.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center text-sm text-text-mid font-bold">
                    {product.moq}
                  </td>
                ))}
              </tr>

              {/* Action Row */}
              <tr className="border-t border-border bg-bg-light">
                <td className="px-6 py-4 font-bold text-navy text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="ChatBubbleLeftRightIcon" size={16} />
                    Inquiry
                  </div>
                </td>
                {products.map((product) => (
                  <td key={product.id} className="px-6 py-4 text-center">
                    <a
                      href="https://wa.me/85259773386"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark text-white px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all"
                    >
                      <Icon name="ChatBubbleLeftRightIcon" size={12} variant="solid" />
                      Get Quote
                    </a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="bg-bg-light px-8 py-4 border-t border-border flex items-center justify-between">
          <p className="text-xs text-text-muted">
            <Icon name="InformationCircleIcon" size={14} className="inline mr-1" />
            Contact us for detailed pricing and availability
          </p>
          <button
            onClick={onClose}
            className="flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-6 py-2.5 rounded-full font-bold text-sm transition-all"
          >
            Close Comparison
          </button>
        </div>
      </div>
    </div>
  );
}